import type { MeQuery, WarehouseListItemFragment } from "./graphql"
import { UserProductOrderStatus } from "./graphql"

export const getInitialOrderQueryParams = (args?: {
  me: MeQuery["meAdmin"]
  warehouses: WarehouseListItemFragment[]
}) => {
  if (!args) return

  const { me, warehouses } = args

  const params = new URLSearchParams()

  params.set("status", UserProductOrderStatus.Accepted)
  params.set("charged", "true")
  params.set("onHold", "false")

  const warehouseId =
    !me?.warehouseId &&
    warehouses.find((warehouse) => warehouse.codeName === "SUPLIFUL")?.id

  if (warehouseId) {
    params.set("warehouseId", warehouseId)
  }

  return params.toString()
}

export const routes = {
  home: `/`,
  admins: `/admins`,
  carriers: `/fulfillment-settings/carriers`,
  orders: (args?: {
    me: MeQuery["meAdmin"]
    warehouses: WarehouseListItemFragment[]
  }) => {
    const params = getInitialOrderQueryParams(args)
    return params ? `/orders?${params}` : `/orders`
  },
  todaysItems: (args?: {
    me: MeQuery["meAdmin"]
    warehouses: WarehouseListItemFragment[]
  }) => {
    const params = getInitialOrderQueryParams(args)
    return params ? `/todays-items?${params}` : `/todays-items`
  },
  partnerOrders: `/partner/orders`,
  returnedOrders: "/returned-orders",
  order: (id: string) => `/orders/${id}`,
  partnerOrder: (id: string) => `/partner/orders/${id}`,
  products: `/products`,
  product: (slug: string) => `/products/${slug}`,
  extendedProducts: `/extended-products`,
  extendedProduct: (slug: string) => `/extended-products/${slug}`,
  productsStock: `/catalog/products-stock`,
  productVariants: `/product-variants`,
  purchaseOrder: (id: string) => `/suppliers/${id}/purchase-order`,
  shippingPackages: `/shipping-packages`,
  userProducts: `/user-products`,
  userProduct: (id: string) => `/user-products/${id}`,
  users: `/users`,
  user: (id: string) => `/users/${id}`,
  labelRolls: (args?: {
    me: MeQuery["meAdmin"]
    warehouses: WarehouseListItemFragment[]
  }) => {
    const warehouseIdFilter = () => {
      if (!args) return
      const { me, warehouses } = args
      const adminWarehouseId = me?.warehouseId
      if (adminWarehouseId) return
      const suplifulWarehouseId = warehouses.find(
        (warehouse) => warehouse.codeName === "SUPLIFUL",
      )?.id
      return suplifulWarehouseId || warehouses[0]?.id
    }

    const warehouseId = warehouseIdFilter()
    return warehouseId
      ? `/fulfillment/label-rolls?warehouseId=${warehouseId}`
      : `/fulfillment/label-rolls`
  },
  labeling: (batchId?: string) =>
    batchId
      ? `/fulfillment/labeling?batchId=${batchId}`
      : "/fulfillment/labeling",
  labelSizes: `/catalog/label-sizes`,
  labelVerification: `/user-management/label-verification`,
  labelTransitions: `/user-management/label-transitions`,
  postPayments: `/user-management/post-payments`,
  batchPrinting: `/fulfillment/batch-printing?isSingleItemOrder=true`,
  sorting: `/fulfillment/sorting`,
  bulkShipping: `/fulfillment/bulk-shipping`,
  bulkShippingDetails: (bulkShippingId?: string) =>
    bulkShippingId
      ? `/fulfillment/bulk-shipping-details?bulkShippingId=${bulkShippingId}`
      : "/fulfillment/bulk-shipping-details",
  shipmentBatches: `/fulfillment/shipment-batches`,
  completedShipments: `/fulfillment/completed-shipments`,
  packaging: `/fulfillment-settings/packaging`,
  warehouseShelves: `/fulfillment-settings/warehouse-shelves`,
  warehouses: `/fulfillment-settings/warehouses`,
  catalogBanners: `/site-settings/catalog-banners`,
  systemNotifications: `/site-settings/system-notifications`,
  digitalServiceProducts: `/site-settings/digital-service-products`,
  amazonFba: `/fulfillment/amazon-fba`,
  amazonFbaInvoices: `/user-management/amazon-fba-invoices`,
  bulkInvoices: `/user-management/bulk-invoices`,
}

export const isActive = (asPath: string, href: string) => {
  if (href === "/") {
    return asPath === href
  }

  const asPathURL = new URL(asPath, window.location.origin)
  const hrefURL = new URL(href, window.location.origin)

  return (
    asPathURL.pathname === hrefURL.pathname ||
    asPathURL.pathname.startsWith(hrefURL.pathname + "/")
  )
}
